import React, { useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Facebook, Instagram, Link2, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '../../../context/AuthContext';
import Eyebrow from '../Eyebrow';
import { NewTabHint, newTabRel } from '../linkParts';
import {
  SITE_FOOTER_EMAIL, SITE_FOOTER_GROUPS, SITE_FOOTER_LEGAL, SITE_FOOTER_SERVICE_AREA, SITE_FOOTER_SOCIAL,
} from './footerLinks';

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook };

/** The path part of a footer link ("/how-it-works#patrons" → "/how-it-works"), for aria-current. */
const pathOf = (to) => to.split(/[?#]/)[0];

/**
 * A footer navigation link / the Sign in button (same look): DM Sans 14px navy, rust on hover;
 * a 44px touch target below 800px (32px from there, where a pointer is the norm).
 * `max-w-full` + `overflow-wrap: anywhere`: a word longer than its column (only ever with
 * enlarged text) breaks instead of pushing the page sideways; at the default size every
 * label fits its column, so nothing changes.
 */
export const FOOTER_LINK_CLASS = cn(
  'inline-flex min-h-11 min-w-0 max-w-full cursor-pointer items-center bg-transparent p-0 text-left font-body text-sm leading-[1.55] text-navy no-underline [overflow-wrap:anywhere]',
  'underline-offset-4 transition-colors duration-180 ease-out hover:text-brand-rust hover:underline aria-[current=page]:underline nav:min-h-8',
);

const LEGAL_LINK_CLASS = 'inline-flex min-h-6 items-center text-[0.8125rem] text-brand-muted underline-offset-4 transition-colors duration-180 ease-out hover:text-brand-rust hover:underline';

const SOCIAL_LINK_CLASS = cn(
  'inline-grid size-11 place-items-center border border-brand-rule bg-brand-sheet text-navy shadow-site [&_svg]:size-[18px]',
  'transition-[transform,box-shadow,border-color] duration-180 ease-out hover:border-navy hover:shadow-site-hover motion-safe:hover:-translate-y-0.5',
);

/**
 * The redesigned site footer (approved Sept 26 2026 package, `.footer` in style.css /
 * flair.css): a sunk paper band (paper + 20% rule, 1px rule top edge) across the 1440px
 * canvas. Logo with the service-area line under it ("Starting in Medford, NJ · Serving
 * South Jersey") and, for signed-out visitors, a "Sign in" button (the header's AuthModal,
 * login mode); the "Local connections. / One coaster at a time." line; the footer navigation
 * in two labelled groups ("For guests" / "For businesses & venues", SITE_FOOTER_GROUPS;
 * upgrade brief §2.1; the labels are Plex eyebrows naming their lists, not headings, so the
 * page outline stays the page's own); the handwritten Caveat mailto note, the social links
 * (= the Organization JSON-LD sameAs, SITE_FOOTER_SOCIAL) and the bottom row: © year
 * Tappd, Privacy, Terms. Preview-only items of the package are left out.
 *
 * Layout (the reference's breakpoints): four columns from 1150px (logo · line · the two
 * groups side by side · note), three from 800px (the note wraps to its own row), two below
 * 800px (the nav takes a full row, the two groups still side by side).
 *
 * A `.site-light` surface: focus rings are rust (≥5:1 on the band). Rendered by
 * components/Footer.jsx when SLOT_MODEL is on; `className` goes on the <footer>.
 */
export default function SiteFooter({ className }) {
  const { pathname } = useLocation();
  const auth = useAuth() || {};
  const uid = useId().replace(/:/g, '');
  const showSignIn = !auth.isAuthenticated && !auth.loading && typeof auth.openAuthModal === 'function';
  const year = new Date().getFullYear();

  return (
    <footer className={cn('site-light border-t border-brand-rule bg-brand-paper text-navy', className)}>
      <div className="bg-brand-rule/20">
        <div className="mx-auto w-full max-w-[calc(1680px+5rem)] px-6 pb-[22px] pt-10 [container-type:inline-size] nav:px-10 nav:pb-[23px] nav:pt-[55px]">
          {/* One column when the text is enlarged (an em query: under 320px at 16px text,
              under 640px at 32px): two columns cannot hold "connections." or "Businesses".
              The same in the band's own em (a container query: under 12.5em of content, never
              at the default size, where it is at least 17em) catches enlarged text the media
              query does not see (page CSS, a text-only zoom). */}
          <div className="mb-[52px] grid grid-cols-2 items-start gap-[26px] nav:grid-cols-3 nav:gap-[30px] min-[1150px]:grid-cols-[1fr_1fr_1.2fr_1fr] [@media(max-width:19.9375em)]:grid-cols-1 [@container(max-width:12.5em)]:grid-cols-1">
            <div className="min-w-0">
              <Link to="/" aria-label="Tappd home" className="inline-block w-fit">
                <img
                  src="/tappd-logo.png"
                  alt="Tappd"
                  width="150"
                  height="51"
                  loading="lazy"
                  decoding="async"
                  className="block h-auto w-[125px] nav:w-[140px]"
                />
              </Link>
              <p data-footer-service-area="" className="m-0 mt-3.5 flex items-start gap-1.5 text-sm leading-[1.55] text-navy">
                <MapPin aria-hidden="true" size={16} strokeWidth={1.75} className="mt-[3px] shrink-0" />
                <span className="min-w-0 [overflow-wrap:anywhere]">{SITE_FOOTER_SERVICE_AREA}</span>
              </p>
              {showSignIn ? (
                <button
                  type="button"
                  onClick={() => auth.openAuthModal('login')}
                  className={cn(FOOTER_LINK_CLASS, 'mt-1 font-semibold underline decoration-1')}
                >
                  Sign in
                </button>
              ) : null}
            </div>

            <p className="m-0 text-sm leading-[1.55] text-brand-muted">
              Local connections.
              <br />
              One coaster at a time.
            </p>

            {/* The nav is a size container: its two groups sit side by side only when the nav
                is at least 18em of its own text wide (288px at 16px: phones from 360px, and
                from ~1150px wide screens), otherwise they stack (800-1149px, where the nav is
                one of three columns, and whenever text is enlarged, by any means). Each group
                is a list named by its label (a Plex eyebrow, not a heading). */}
            <nav
              aria-label="Footer"
              className="col-span-2 [container-type:inline-size] nav:col-span-1 [@media(max-width:19.9375em)]:col-span-1 [@container(max-width:12.5em)]:col-span-1"
            >
              <div className="grid grid-cols-1 items-start gap-x-6 gap-y-5 nav:-mt-[5px] [@container(min-width:18em)]:grid-cols-2">
                {SITE_FOOTER_GROUPS.map((group) => {
                  const labelId = `site-footer-${uid}-${group.key}`;
                  return (
                    <div key={group.key} className="min-w-0">
                      <Eyebrow as="p" id={labelId} tone="muted" className="m-0 mb-1.5">
                        {group.label}
                      </Eyebrow>
                      <ul aria-labelledby={labelId} className="m-0 grid list-none gap-y-0.5 p-0">
                        {group.links.map((link) => (
                          <li key={link.to}>
                            <Link
                              to={link.to}
                              aria-current={pathname === pathOf(link.to) ? 'page' : undefined}
                              className={FOOTER_LINK_CLASS}
                            >
                              {link.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </nav>

            <div className="col-span-2 flex flex-wrap items-center gap-x-8 gap-y-6 nav:col-span-3 min-[1150px]:col-span-1 min-[1150px]:block [@media(max-width:19.9375em)]:col-span-1 [@container(max-width:12.5em)]:col-span-1">
              <a
                href={`mailto:${SITE_FOOTER_EMAIL}`}
                className="inline-block max-w-full -rotate-[4deg] font-hand text-[1.6875rem] font-medium leading-[1.05] text-navy transition-colors duration-180 ease-out [overflow-wrap:anywhere] hover:text-brand-rust nav:text-[1.625rem]"
              >
                {SITE_FOOTER_EMAIL}
                <span aria-hidden="true">{' ↗'}</span>
              </a>
              <ul className="flex gap-2.5 min-[1150px]:mt-8">
                {SITE_FOOTER_SOCIAL.map((s) => {
                  const Icon = SOCIAL_ICONS[s.id] || Link2;
                  return (
                    <li key={s.href}>
                      <a href={s.href} target="_blank" rel={newTabRel('_blank')} className={SOCIAL_LINK_CLASS}>
                        <Icon aria-hidden="true" strokeWidth={1.75} />
                        <span className="sr-only">{`Tappd on ${s.name}`}</span>
                        <NewTabHint target="_blank" />
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-[15px] border-t border-brand-rule pt-[21px] text-[0.6875rem] text-brand-muted nav:gap-[22px]">
            <p className="m-0">{`© ${year} Tappd`}</p>
            <ul className="ml-auto flex gap-[25px] nav:ml-0">
              {SITE_FOOTER_LEGAL.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    aria-current={pathname === link.to ? 'page' : undefined}
                    className={LEGAL_LINK_CLASS}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </footer>
  );
}
